import React, { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLocaleFromPath } from '../components/LocaleLink';

const translations = {
  en: {
    nav: {
      home: 'Home',
      products: 'Products',
      features: 'Features',
      pricing: 'Pricing',
      about: 'About',
      contact: 'Contact',
      login: 'Sign In',
      signup: 'Start Free',
      dashboard: 'Dashboard',
    },
    footer: {
      tagline: 'AI-powered multilingual intelligence and publishing infrastructure.',
      products: 'Products',
      company: 'Company',
      legal: 'Legal',
      support: 'Support',
      links: {
        quidpulse: 'QuidPulse',
        quidanalyzer: 'Quidanalyzer',
        postPublisher: 'Post Publisher',
        videoPublisher: 'Video Publisher',
        about: 'About',
        features: 'Features',
        pricing: 'Pricing',
        contact: 'Contact',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        cookie: 'Cookie Policy',
        dataDeletion: 'Data Deletion',
        support: 'Support Center',
      },
      copyright: '© 2026 Quidory. All rights reserved.',
      legalEntity: 'Operated by Al Yakhour LLC · Wyoming, USA',
    },
    common: {
      learnMore: 'Learn more',
      getStarted: 'Get Started',
      bookDemo: 'Book Demo',
      startFree: 'Start Free',
      contactSales: 'Contact Sales',
      backToHome: 'Back to Home',
      comingSoon: 'Coming Soon',
      current: 'Current',
      popular: 'Most Popular',
    },
    auth: {
      login: {
        headline: 'Welcome Back',
        subheadline: 'Sign in to your Quidory account',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        password: 'Password',
        passwordPlaceholder: '••••••••',
        submit: 'Sign In',
        forgot: 'Forgot password?',
        noAccount: "Don't have an account?",
        signup: 'Sign up',
        demo: 'Try Demo Dashboard',
      },
      signup: {
        headline: 'Create Your Account',
        subheadline: 'Start your Quidory journey',
        name: 'Full Name',
        namePlaceholder: 'Your full name',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        password: 'Password',
        passwordPlaceholder: 'Create a strong password',
        submit: 'Create Account',
        hasAccount: 'Already have an account?',
        login: 'Sign in',
        terms: 'By creating an account, you agree to our',
        termsLink: 'Terms of Service',
        and: 'and',
        privacyLink: 'Privacy Policy',
      },
    },
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      products: 'المنتجات',
      features: 'المزايا',
      pricing: 'الأسعار',
      about: 'عن كويدوري',
      contact: 'تواصل معنا',
      login: 'تسجيل الدخول',
      signup: 'ابدأ مجاناً',
      dashboard: 'لوحة التحكم',
    },
    footer: {
      tagline: 'بنية تحتية متعددة اللغات للذكاء الاصطناعي والنشر الآلي.',
      products: 'المنتجات',
      company: 'الشركة',
      legal: 'القانونية',
      support: 'الدعم',
      links: {
        quidpulse: 'QuidPulse',
        quidanalyzer: 'Quidanalyzer',
        postPublisher: 'Post Publisher',
        videoPublisher: 'Video Publisher',
        about: 'عن كويدوري',
        features: 'المزايا',
        pricing: 'الأسعار',
        contact: 'تواصل معنا',
        privacy: 'سياسة الخصوصية',
        terms: 'شروط الخدمة',
        cookie: 'سياسة الكوكيز',
        dataDeletion: 'حذف البيانات',
        support: 'مركز الدعم',
      },
      copyright: '© ٢٠٢٦ كويدوري. جميع الحقوق محفوظة.',
      legalEntity: 'تُدار بواسطة Al Yakhour LLC · وايومنغ، الولايات المتحدة',
    },
    common: {
      learnMore: 'اعرف أكثر',
      getStarted: 'ابدأ الآن',
      bookDemo: 'احجز عرضاً',
      startFree: 'ابدأ مجاناً',
      contactSales: 'تواصل مع المبيعات',
      backToHome: 'العودة للرئيسية',
      comingSoon: 'قريباً',
      current: 'الحالي',
      popular: 'الأكثر شعبية',
    },
    auth: {
      login: {
        headline: 'مرحباً بعودتك',
        subheadline: 'سجّل الدخول إلى حساب كويدوري',
        email: 'البريد الإلكتروني',
        emailPlaceholder: 'your@email.com',
        password: 'كلمة المرور',
        passwordPlaceholder: '••••••••',
        submit: 'تسجيل الدخول',
        forgot: 'نسيت كلمة المرور؟',
        noAccount: 'ليس لديك حساب؟',
        signup: 'سجّل الآن',
        demo: 'جرّب لوحة التحكم التجريبية',
      },
      signup: {
        headline: 'أنشئ حسابك',
        subheadline: 'ابدأ رحلتك مع كويدوري',
        name: 'الاسم الكامل',
        namePlaceholder: 'اسمك الكامل',
        email: 'البريد الإلكتروني',
        emailPlaceholder: 'your@email.com',
        password: 'كلمة المرور',
        passwordPlaceholder: 'أنشئ كلمة مرور قوية',
        submit: 'إنشاء الحساب',
        hasAccount: 'لديك حساب بالفعل؟',
        login: 'سجّل الدخول',
        terms: 'بإنشاء حساب، فإنك توافق على',
        termsLink: 'شروط الخدمة',
        and: 'و',
        privacyLink: 'سياسة الخصوصية',
      },
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Source of truth: URL path prefix (/ar/... or /en/...).
  // For the root '/' or any unprefixed path, fall back to localStorage hint, then 'ar'.
  const language = (() => {
    const fromUrl = getLocaleFromPath(pathname);
    if (pathname.startsWith('/ar') || pathname.startsWith('/en')) return fromUrl;
    // unprefixed path (e.g. '/dashboard') — use stored hint
    if (typeof window === 'undefined') return 'ar';
    try {
      const stored = window.localStorage.getItem('quidory.language');
      return stored === 'en' || stored === 'ar' ? stored : 'ar';
    } catch {
      return 'ar';
    }
  })();

  const toggleLanguage = () => {
    const next = language === 'ar' ? 'en' : 'ar';
    try { window.localStorage.setItem('quidory.language', next); } catch { /* ignore */ }
    // If currently on a locale-prefixed route, swap the prefix; otherwise stay put.
    if (pathname.startsWith('/ar/') || pathname === '/ar') {
      navigate(pathname.replace(/^\/ar/, '/en') || '/en');
    } else if (pathname.startsWith('/en/') || pathname === '/en') {
      navigate(pathname.replace(/^\/en/, '/ar') || '/ar');
    } else {
      // unprefixed (e.g. /dashboard) — just persist and re-render via the document attrs effect
      // language will pick up from localStorage on next render of pages that read it
      // To force re-render of consumers without changing URL, we no-op here; consumers re-render
      // because we keep language derived. For unprefixed routes the language toggle is mainly visual.
      // We trigger a tiny location replace to nudge re-render.
      navigate(pathname, { replace: true });
    }
  };

  // Sync localStorage with the URL-derived language so the next visit to '/' picks the right default.
  useEffect(() => {
    try { window.localStorage.setItem('quidory.language', language); } catch { /* ignore */ }
  }, [language]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    return (value !== null && value !== undefined) ? String(value) : key;
  };

  return (
    <LanguageContext.Provider value={{
      language,
      toggleLanguage,
      t,
      isRTL: language === 'ar',
      dir: language === 'ar' ? 'rtl' : 'ltr',
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
